
// base
import { Component, Vue, Prop, Watch } from "vue-property-decorator"
import { v4 as generateUuid } from "uuid"

// apollo
import * as cdn from "@coredination/components"
import CustomFormQuery from "@/graphql/customform/customform.graphql"
import CustomFormTemplatesQuery from "@/graphql/customform/customformtemplates.graphql"
import UpdateCustomForm from "@/graphql/customform/updatecustomform.graphql"
import DeleteCustomForm from "@/graphql/customform/deletecustomform.graphql"
import JobDetailQuery from "@/graphql/job/jobdetail.graphql"
import GroupsQuery from "@/graphql/group/groups.graphql"
import { navbar, NavbarOption } from "@/components/NavbarOptions.vue"

@Component({
    apollo: {
        customForm: {
            query: CustomFormQuery,
            variables() {
                return { uuid: this.uuid ? this.uuid : this.formTemplateId, jobUuid: this.jobUuid ? this.jobUuid : null}
            },
            skip() {
                return !this.uuid && !this.formTemplateId
            },
            result(result: any) {
                if (!(result && result.error)) {
                    this.localCustomForm = JSON.parse(JSON.stringify(this.customForm))
                    this.editing = this.customForm.state != "COMPLETE" && this.customForm.state != "SIGNED_OFF"
                }
            }
        },
        customFormTemplates: {
            query: CustomFormTemplatesQuery,
            result(result: any) {
                // In most cases the above CustomFormQuery will be used to get the custom form or its template.
                // But in some cases with complex permissions we can end up in situations where the user is trying to create a new form but
                // not is allowed to fetch the template directly and instead has to fetch all templates and find the one they want.
                if (!(result && result.error) && !this.uuid) {
                    if (this.customFormTemplates!.length > 0) {
                        let customForm: any = this.customFormTemplates.find((template: any) => template.id == this.formTemplateId)
                        if (customForm) {
                            this.customForm = customForm
                            this.localCustomForm = JSON.parse(JSON.stringify(this.customForm))
                        }
                    }
                }
            }
        },
        me: cdn.graphql.me,
        groups: GroupsQuery,
        job: {
            query: JobDetailQuery,
            skip() {
                return !this.jobUuid && !this.model.jobUuid
            },
            variables() {
                return { uuid: this.jobUuid || this.model.jobUuid }
            },
        }
    },
    components: {
        CustomFieldsField: () => import("@/components/fields/CustomFieldsField.vue"),
        DeleteDialog: () => import("@/components/dialogs/DeleteDialog.vue"),
        ShareDialog: () => import("@/components/dialogs/ShareDialog.vue"),
    },
})
export default class CustomFormEdit extends Vue {
    @Prop() uuid?: string
    @Prop() formTemplateId?: string
    @Prop() jobUuid?: string
    @Prop() new?: boolean
    get isNew() { return this.new }
    showDeleteModal: boolean = false
    editing: boolean = true
    job?: any
    me: any = {}
    customForm: any = {}
    customFormTemplates: any[] = []
    // Changes are made to a local copy of the customForm so we can know what is stored in the database and what our local changes are
    localCustomForm: any = {}
    groups?: any[] = []

    get model() {
        return this.localCustomForm
    }
    set model(value: any) {
        this.localCustomForm = value
    }
    get editable(): boolean {
        // insert unneccesarily complex authorization logic here
        if (this.me && this.me.permissions)
            return (this.me.permissions!.includes("edit_all_data")
                || this.me.permissions.includes("admin_forms")
                || this.me.permissions.includes('create_forms'))
        return false;
    }
    temporary: boolean = false
    get deleteButtonText() {
        return this.temporary ? this.$t("cancel") : this.$t("delete")
    }

    warnDialog: boolean = false

    save(overrideRequired: any) {
        if (this.$refs && this.$refs.customFieldsField && typeof this.$refs.customFieldsField == "object") {
            var customFieldsField = Object(this.$refs.customFieldsField)
                if (!customFieldsField.canBeSaved() && overrideRequired !== true) {
                    //;(this as any).$toast.error(this.$t("required_field_error"), { icon: 'warning' })
                    this.warnDialog = true
                    return false
                }
        }
        const input = {
            uuid: this.new ? generateUuid() : (this.localCustomForm.uuid || null),
            fromTemplateId: this.formTemplateId || null,
            state: this.localCustomForm.state,
            fields: this.stripTypeNameFromCustomFields(this.localCustomForm.fields) || null,
            jobUuid: this.localCustomForm.jobUuid || this.jobUuid || null,
            description: this.localCustomForm.description || null,
            instructions: this.localCustomForm.instructions || null,
            title: this.localCustomForm.title || null
        }
        var self = this
        return this.$apollo
            .mutate({
                mutation: UpdateCustomForm,
                variables: { input },
                update(proxy, response) {
                    const hackProxy = proxy as any
                    Object.keys(hackProxy.data.data).forEach(key => {
                        if (key.match(/(job|myJobs|myWorkStats|JobListItem)/)) hackProxy.data.delete(key)
                        if (key.match(/(customForm|updateCustomForm)/)) hackProxy.data.delete(key)
                    })
                }
            })
            .then(async response => {
                console.log("custom form saved", response)
                Object.assign(this.localCustomForm, response.data.updateCustomForm)
                if (!self.temporary) self.$router.back()
            })
            .catch(error => { console.log(error) })
    }

    clickDelete() {
        if (this.temporary) this.deleteForm()
        else this.showDeleteModal = !this.showDeleteModal
    }

    deleteForm() {
        var self: any = this
        var uuid: string = this.customForm.uuid || null
        if (!uuid) return

        return this.$apollo
            .mutate({
                mutation: DeleteCustomForm,
                variables: { uuid },
                update(proxy, response) {
                    const hackProxy = proxy as any
                    Object.keys(hackProxy.data.data).forEach(key => {
                        if (key.match(/(job|myJobs|myWorkStats|JobListItem)/)) hackProxy.data.delete(key)
                        if (key.match(/(customForm|updateCustomForm)/)) hackProxy.data.delete(key)
                    })
                }
            })
            .then(async response => {
                console.log("Customform deleted", response)
                self.$router.back()
            })
    }

    get loading() {
        return this.$apollo.queries.customForm.loading
    }

    // i hate this stupid hacky solution but i can't figure out a better solution
    // to the __typename-situation. screw it, i'll leave this until we've figured it out
    stripTypeNameFromCustomFields(customFields: any[]) {
        if (!customFields || customFields.length == 0) return null
        return customFields.map((custom: any) => {
            const { ["__typename"]: _, ...cleanCustomFields } = custom
            if (cleanCustomFields.document) {
                const { ["__typename"]: __, ...cleanDocument } = cleanCustomFields.document;
                cleanCustomFields.document = cleanDocument;
            }
            return cleanCustomFields
        })
    }

    edit() {
        this.editing = true
    }

    sharing: boolean = false
    showShareDialog() {
        this.sharing = true
    }

    get options(): NavbarOption[] {
        if (!this.me || !this.me.permissions) return []
        var options = []
        if (this.editing) {
            options.push({
                icon: "done", action: this.save
            })
        } else {
            options.push({
                icon: "edit", action: this.edit
            })
            options.push({
                title: this.$t("customFormOptions_shareCustomForm"),
                action: this.showShareDialog
            })
        }
        if (!this.new) {
            options.push({
                title: this.$t("delete"),
                action: this.clickDelete,
            })
        }
        return options
    }

    get groupConfiguration(): any {
        if (!this.groups || !this.job || !this.job.groupUuid) return false
        var group = this.groups?.find((group) => group.uuid === this.job.groupUuid)
        return group && group.configuration && JSON.parse(group.configuration)
    }

    @Watch("me")
    @Watch("editing")
    watchOptions() {
        if (this.me != null) {
            navbar?.setOptions(this.options)
        }
    }

    iosWrapperClient() {
        return Vue.prototype.$iosWrapperClient
    }
}
